/**
 * Behavioral Assessment — shared types + thin API client.
 *
 * Base path: /api/behavioral-assessments (all authenticated).
 * IMPORTANT: every call goes through authFetch so the X-CBO-ID header is sent
 * (see lib/authFetch.ts). Never use raw fetch here.
 *
 * DISCLAIMER: instruments here use PROVISIONAL norms and are NOT validated
 * clinical instruments. UI must surface this. The assistive AI does not
 * interpret results.
 */

import { authFetchJson } from '@/lib/authFetch';

const BASE = '/api/behavioral-assessments';

// ── Scale categories ─────────────────────────────────────────────
export type ScaleCategory = 'validity' | 'clinical' | 'treatment' | 'interpersonal';

export type ValidityStatus = 'valid' | 'caution' | 'invalid';

export type AdministrationStatus =
  | 'created'
  | 'in_progress'
  | 'submitted'
  | 'scored'
  | 'released'
  | string;

// ── Instrument catalog ───────────────────────────────────────────
export interface InstrumentSummary {
  code: string;
  name: string;
  description: string;
  version: string;
  total_items: number;
  is_system: boolean;
  provisional_norms: boolean;
  status: string;
}

export interface ResponseOption {
  code: string; // e.g. "F", "ST", "MT", "VT" — this is what raw_response uses
  label: string;
  value: number;
}

export interface InstrumentScale {
  code: string;
  name: string;
  category: ScaleCategory;
  description: string;
  is_safety_critical: boolean;
  display_order: number;
}

export interface InstrumentItem {
  item_number: number;
  prompt: string;
  item_role: string;
  display_order: number;
}

export interface InstrumentDetail {
  instrument: {
    code: string;
    name: string;
    version: string;
    response_options: ResponseOption[];
    total_items: number;
    provisional_norms: boolean;
  };
  scales: InstrumentScale[];
  items: InstrumentItem[];
}

// ── Administrations ──────────────────────────────────────────────
export interface CreateAdministrationResult {
  administration_id: string;
  public_token: string;
  status: AdministrationStatus;
}

/** A row from GET /administrations?member_id={id} (list view). */
export interface MemberAdministration {
  id: string;
  member_id: string | null;
  status: AdministrationStatus;
  validity_status: ValidityStatus | null;
  is_simulation: boolean;
  started_at: string | null;
  submitted_at: string | null;
  scored_at: string | null;
  released_at: string | null;
  instrument_code: string;
  instrument_name: string;
  first_name: string | null;
  last_name: string | null;
  elevated_count: number;
  open_safety_events: number;
}

export interface ListAdministrationsResult {
  administrations: MemberAdministration[];
}

export interface CrisisResources {
  // Backend shape is intentionally loose; we render whatever it sends plus a
  // hardcoded safety-net (988 / Crisis Text Line) regardless.
  [key: string]: unknown;
}

export interface SaveResponsesResult {
  saved: boolean;
  safety_triggered: boolean;
  crisis_resources?: CrisisResources | null;
}

export interface SubmitResult {
  administration_id: string;
  status: AdministrationStatus;
  validity_status: ValidityStatus;
  validity_flags: string[];
  safety_triggered: boolean;
  note?: string;
}

export interface ProfileScale {
  scale_code: string;
  scale_name: string;
  category: ScaleCategory;
  raw_score: number;
  t_score: number;
  band: string;
  elevated: boolean;
  items_answered: number;
}

export interface ProfileThresholds {
  midline: number;
  mild: number;
  elevated: number;
  marked: number;
}

export interface ProfileResult {
  administration_id: string;
  status: AdministrationStatus;
  validity_status: ValidityStatus;
  is_simulation: boolean;
  scales: ProfileScale[];
  thresholds: ProfileThresholds;
}

// ── Simulation ───────────────────────────────────────────────────
export interface SimulateScale {
  scale_code: string;
  raw_score: number;
  t_score: number;
  band: string;
  elevated: boolean;
}

export interface SimulateResult {
  is_simulation: boolean;
  validity_status: ValidityStatus;
  safety_triggered: boolean;
  scales: SimulateScale[];
}

// ── Safety events ────────────────────────────────────────────────
export interface SafetyEvent {
  id: string;
  administration_id: string;
  member_id: string | null;
  trigger_type: string;
  item_number: number | null;
  severity: string;
  detail: string;
  status: string;
  triggered_at: string;
}

// ── AI interaction ───────────────────────────────────────────────
export interface AiInteractResult {
  reply: string;
  escalated: boolean;
  crisis_resources?: CrisisResources | null;
}

// ── Clinician draft summary (RAG-grounded, clinician-only) ───────
// This is a clinician-facing draft only — NEVER shown to the patient, and it
// is not a diagnosis.
export interface ClinicianSummary {
  id: string;
  draft_text: string;
  /** Present once a clinician has edited the draft. */
  edited_text?: string | null;
  model: string;
  rag_used: boolean;
  status: string;
  generated_at: string;
  edited_at?: string | null;
  /** Disclaimer text returned by the backend (present on POST; may be absent on GET). */
  disclaimer?: string | null;
}

export interface GetClinicianSummaryResult {
  summary: ClinicianSummary | null;
}

// ── Response payloads sent to the backend ────────────────────────
export interface ItemResponse {
  item_number: number;
  raw_response: string; // the option `code`, NOT the numeric value
}

// ── Default (hardcoded) crisis resources ─────────────────────────
// Always shown when safety is triggered, independent of the backend payload.
export const CRISIS_RESOURCES_FALLBACK = [
  {
    name: '988 Suicide & Crisis Lifeline',
    detail: 'Call or text 988 (24/7, free, confidential).',
    href: 'tel:988',
    action: 'Call or text 988',
  },
  {
    name: 'Crisis Text Line',
    detail: 'Text HOME to 741741 to connect with a crisis counselor.',
    href: 'sms:741741&body=HOME',
    action: 'Text HOME to 741741',
  },
  {
    name: 'Emergency',
    detail: 'If you are in immediate danger, call 911.',
    href: 'tel:911',
    action: 'Call 911',
  },
] as const;

// ── Thresholds fallback (mirrors backend defaults) ───────────────
export const DEFAULT_THRESHOLDS: ProfileThresholds = {
  midline: 50,
  mild: 60,
  elevated: 70,
  marked: 80,
};

// ── API client ───────────────────────────────────────────────────
export const behavioralAssessmentApi = {
  listInstruments(): Promise<{ instruments: InstrumentSummary[] }> {
    return authFetchJson(`${BASE}/instruments`);
  },

  getInstrument(code: string): Promise<InstrumentDetail> {
    return authFetchJson(`${BASE}/instruments/${encodeURIComponent(code)}`);
  },

  createAdministration(body: {
    instrument_code: string;
    member_id?: string | null;
    is_simulation?: boolean;
  }): Promise<CreateAdministrationResult> {
    return authFetchJson(`${BASE}/administrations`, {
      method: 'POST',
      jsonBody: body,
    });
  },

  /** List a member's administrations. Simulations are excluded by default. */
  listAdministrations(
    memberId: string | number,
    opts: { status?: string; includeSimulations?: boolean } = {},
  ): Promise<ListAdministrationsResult> {
    const params = new URLSearchParams({ member_id: String(memberId) });
    if (opts.status) params.set('status', opts.status);
    if (opts.includeSimulations) params.set('include_simulations', 'true');
    return authFetchJson(`${BASE}/administrations?${params.toString()}`);
  },

  saveResponses(
    administrationId: string,
    body: { responses: ItemResponse[]; completion_time_seconds?: number },
  ): Promise<SaveResponsesResult> {
    return authFetchJson(
      `${BASE}/administrations/${encodeURIComponent(administrationId)}/responses`,
      { method: 'PUT', jsonBody: body },
    );
  },

  submit(administrationId: string): Promise<SubmitResult> {
    return authFetchJson(
      `${BASE}/administrations/${encodeURIComponent(administrationId)}/submit`,
      { method: 'POST' },
    );
  },

  getProfile(administrationId: string): Promise<ProfileResult> {
    return authFetchJson(
      `${BASE}/administrations/${encodeURIComponent(administrationId)}/profile`,
    );
  },

  simulate(body: {
    instrument_code: string;
    target_elevations: Record<string, number>;
    seed?: number;
  }): Promise<SimulateResult> {
    return authFetchJson(`${BASE}/administrations/simulate`, {
      method: 'POST',
      jsonBody: body,
    });
  },

  listSafetyEvents(status: string = 'open'): Promise<{ events: SafetyEvent[] }> {
    return authFetchJson(
      `${BASE}/safety-events?status=${encodeURIComponent(status)}`,
    );
  },

  aiInteract(
    administrationId: string,
    message: string,
  ): Promise<AiInteractResult> {
    return authFetchJson(
      `${BASE}/administrations/${encodeURIComponent(administrationId)}/ai/interact`,
      { method: 'POST', jsonBody: { message } },
    );
  },

  // ── Clinician draft summary (clinician-only, never shown to patient) ──
  getClinicianSummary(administrationId: string): Promise<GetClinicianSummaryResult> {
    return authFetchJson(
      `${BASE}/administrations/${encodeURIComponent(administrationId)}/clinician-summary`,
    );
  },

  generateClinicianSummary(administrationId: string): Promise<ClinicianSummary> {
    return authFetchJson(
      `${BASE}/administrations/${encodeURIComponent(administrationId)}/clinician-summary`,
      { method: 'POST' },
    );
  },

  saveClinicianSummary(
    administrationId: string,
    body: { edited_text: string; finalize: boolean },
  ): Promise<ClinicianSummary> {
    return authFetchJson(
      `${BASE}/administrations/${encodeURIComponent(administrationId)}/clinician-summary`,
      { method: 'PUT', jsonBody: body },
    );
  },
};

// ── Category display helpers ─────────────────────────────────────
export const CATEGORY_ORDER: ScaleCategory[] = [
  'validity',
  'clinical',
  'treatment',
  'interpersonal',
];

export const CATEGORY_LABELS: Record<ScaleCategory, string> = {
  validity: 'Validity',
  clinical: 'Clinical',
  treatment: 'Treatment Considerations',
  interpersonal: 'Interpersonal',
};

export function categoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat as ScaleCategory] ?? cat;
}
